import { createClient, Client } from '@libsql/client/web';
import { isDesktop } from '../utils/platform';
import { SCHEMA, MIGRATIONS } from './schema';
import { filterPendingMigrations } from './migrationUtils';
import { processOutboxQueue } from './syncOutbox';

export type TursoSyncStatus = 'synced' | 'syncing' | 'offline' | 'disabled' | 'error' | 'local_only' | 'fallback_active';

export interface TursoConfig {
  url: string;
  authToken: string;
  enabled: boolean;
}

let activeDbUrl: string = '';

export function getTursoDbUrl(): string {
  return activeDbUrl;
}

// Transformar libsql:// a https:// para peticiones HTTP/REST en browsers
function normalizeTursoUrl(rawUrl: string): string {
  if (!rawUrl) return '';
  return rawUrl.startsWith('libsql://') ? rawUrl.replace('libsql://', 'https://') : rawUrl;
}

// Credenciales gestionadas automáticamente por Clerk
export function getTursoConfig(): TursoConfig {
  if (isDesktop()) {
    const envUrl = (import.meta.env.VITE_TURSO_DATABASE_URL as string) || '';
    const envToken = (import.meta.env.VITE_TURSO_AUTH_TOKEN as string) || '';
    return {
      url: envUrl,
      authToken: envToken,
      enabled: Boolean(envUrl && envToken),
    };
  }

  return {
    url: '',
    authToken: '',
    enabled: false,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Estado global del cliente y suscripciones de status
// ─────────────────────────────────────────────────────────────────────────────

let clientInstance: Client | null = null;
let currentStatus: TursoSyncStatus = 'disabled';
const statusListeners = new Set<(status: TursoSyncStatus) => void>();

export function getTursoStatus(): TursoSyncStatus {
  return currentStatus;
}

export function subscribeTursoStatus(listener: (status: TursoSyncStatus) => void): () => void {
  statusListeners.add(listener);
  listener(currentStatus); // emit current state immediately
  return () => statusListeners.delete(listener);
}

function updateStatus(newStatus: TursoSyncStatus) {
  if (currentStatus === newStatus) return;
  currentStatus = newStatus;
  statusListeners.forEach(l => l(newStatus));
}

// ─────────────────────────────────────────────────────────────────────────────
// Conexión / reconexión del cliente libSQL
// ─────────────────────────────────────────────────────────────────────────────

let isTursoSchemaEnsured = false;

export async function triggerOutboxFlush(): Promise<{ processed: number; remaining: number }> {
  if (!clientInstance || !navigator.onLine) {
    return { processed: 0, remaining: 0 };
  }
  return processOutboxQueue(syncExecuteRemote, syncBatchExecuteRemote, getTursoStatus);
}

function reconnectWithConfig(config: TursoConfig) {
  clientInstance = null;
  isTursoSchemaEnsured = false;

  if (!config.enabled || !config.url || !config.authToken) {
    activeDbUrl = '';
    updateStatus('disabled');
    return;
  }

  try {
    const httpUrl = normalizeTursoUrl(config.url);
    activeDbUrl = httpUrl;
    clientInstance = createClient({ url: httpUrl, authToken: config.authToken });
    updateStatus(navigator.onLine ? 'synced' : 'offline');
    if (SCHEMA) {
      ensureTursoSchema(SCHEMA).then(() => {
        triggerOutboxFlush().catch(() => {});
      }).catch(() => {});
    }
  } catch (err) {
    console.warn('[TursoSync] No se pudo instanciar el cliente:', err);
    updateStatus('error');
  }
}

/**
 * Activa la sincronización con las credenciales personales del usuario de Clerk.
 * En modo Web (isDesktop() === false), esto conecta al cliente remoto directo como fuente primaria.
 */
export function activateUserSync(dbUrl: string, dbToken: string): void {
  if (!dbUrl || !dbToken) {
    console.warn('[TursoSync] activateUserSync: credenciales vacías, ignorando.');
    return;
  }
  console.info(`[TursoSync] Activando sync (${isDesktop() ? 'Desktop Replica' : 'Web Direct Remote'}):`, dbUrl);
  reconnectWithConfig({ url: dbUrl, authToken: dbToken, enabled: true });
}

/**
 * Desactiva la sincronización con Turso.
 */
export function deactivateUserSync(): void {
  console.info('[TursoSync] Usuario deslogueado: desconectando cliente remoto.');
  clientInstance = null;
  activeDbUrl = '';
  isTursoSchemaEnsured = false;
  updateStatus('disabled');
}

// ─────────────────────────────────────────────────────────────────────────────
// Inicialización al cargar el módulo
// ─────────────────────────────────────────────────────────────────────────────

if (typeof window !== 'undefined') {
  if (isDesktop()) {
    const staticConfig = getTursoConfig();
    if (staticConfig.url && staticConfig.authToken && staticConfig.enabled) {
      reconnectWithConfig(staticConfig);
    } else {
      updateStatus('disabled');
    }
  } else {
    // En la Web arranca disabled hasta que el useEffect de MainLayout detecte la sesión de Clerk
    updateStatus('disabled');
  }

  window.addEventListener('online', () => {
    if (clientInstance) {
      updateStatus('synced');
      triggerOutboxFlush().catch(() => {});
    }
  });

  window.addEventListener('offline', () => {
    if (clientInstance) updateStatus('offline');
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// API pública de ejecución (Desktop Async / Web Direct Primary)
// ─────────────────────────────────────────────────────────────────────────────

export function getTursoClient(): Client | null {
  return clientInstance;
}

/**
 * Ejecuta una consulta SELECT directa en Turso (para modo Web cuando no hay SQLite local)
 */
export async function selectRemoteDirect<T>(query: string, params: any[] = []): Promise<T> {
  if (!clientInstance) {
    return [] as unknown as T;
  }
  try {
    updateStatus('syncing');
    const rs = await clientInstance.execute({ sql: query, args: params });
    updateStatus('synced');
    
    const rows = rs.rows.map(row => {
      const obj: Record<string, any> = {};
      rs.columns.forEach((col, idx) => {
        obj[col] = row[idx];
      });
      return obj;
    });
    return rows as unknown as T;
  } catch (err: any) {
    const errMsg = err?.message || String(err);
    const isSchemaError = errMsg.includes('no such table') || errMsg.includes('no column named') || errMsg.includes('has no column named');
    if (isSchemaError && SCHEMA) {
      await ensureTursoSchema(SCHEMA, true);
      try {
        const rs = await clientInstance.execute({ sql: query, args: params });
        updateStatus('synced');
        return rs.rows.map(row => {
          const obj: Record<string, any> = {};
          rs.columns.forEach((col, idx) => { obj[col] = row[idx]; });
          return obj;
        }) as unknown as T;
      } catch (_) {}
    }
    console.error('[TursoSync] Error en selectRemoteDirect:', err);
    updateStatus('error');
    return [] as unknown as T;
  }
}

/**
 * Ejecuta una consulta de modificación en Turso.
 */
export async function syncExecuteRemote(query: string, params: any[] = []): Promise<boolean> {
  if (!clientInstance || !navigator.onLine) {
    if (!navigator.onLine && currentStatus !== 'disabled') updateStatus('offline');
    return false;
  }

  try {
    updateStatus('syncing');
    await clientInstance.batch([
      { sql: 'PRAGMA foreign_keys = ON;', args: [] },
      { sql: query, args: params }
    ], "write");
    updateStatus('synced');
    return true;
  } catch (err: any) {
    const errMsg = err?.message || String(err);
    const isSchemaError = errMsg.includes('no such table') || errMsg.includes('no column named') || errMsg.includes('has no column named');
    if (isSchemaError && SCHEMA) {
      await ensureTursoSchema(SCHEMA, true);
      try {
        await clientInstance.batch([
          { sql: 'PRAGMA foreign_keys = ON;', args: [] },
          { sql: query, args: params }
        ], "write");
        updateStatus('synced');
        return true;
      } catch (_) {}
    }
    const isFkError = errMsg.includes('FOREIGN KEY constraint failed');
    if (isFkError) {
      console.warn('[TursoSync] Omitida restricción de Foreign Key en ejecución remota:', query);
      updateStatus('synced');
      return false;
    }

    console.warn('[TursoSync] Fallo de ejecucion remota Turso:', err);
    updateStatus('offline');
    return false;
  }
}

/**
 * Ejecuta un lote (batch) de consultas de modificación en Turso en peticiones HTTP agrupadas (Ultra Rápido).
 */
export async function syncBatchExecuteRemote(statements: Array<{ sql: string; args: any[] }>): Promise<boolean> {
  if (!clientInstance || !navigator.onLine || statements.length === 0) {
    return false;
  }

  try {
    updateStatus('syncing');
    const CHUNK_SIZE = 50;
    for (let i = 0; i < statements.length; i += CHUNK_SIZE) {
      const chunk = statements.slice(i, i + CHUNK_SIZE);
      const chunkWithFk = [
        { sql: 'PRAGMA foreign_keys = ON;', args: [] },
        ...chunk
      ];
      await clientInstance.batch(chunkWithFk, "write");
    }
    updateStatus('synced');
    return true;
  } catch (err: any) {
    console.warn('[TursoSync] Fallo en batch remoto, realizando fallback individual:', err);
    for (const stmt of statements) {
      await syncExecuteRemote(stmt.sql, stmt.args);
    }
    return false;
  }
}

export async function getTursoTableColumns(tableName: string): Promise<Set<string>> {
  const columns = new Set<string>();
  if (!clientInstance) return columns;
  try {
    const rs = await clientInstance.execute(`PRAGMA table_info(${tableName})`);
    if (rs && Array.isArray(rs.columns) && Array.isArray(rs.rows)) {
      const nameIdx = rs.columns.indexOf('name');
      for (const row of rs.rows) {
        const colName = nameIdx !== -1 ? row[nameIdx] : row[1];
        if (colName !== undefined && colName !== null) {
          columns.add(String(colName).toLowerCase());
        }
      }
    }
  } catch (err) {
    console.warn(`[TursoSync] Error al consultar PRAGMA table_info(${tableName}):`, err);
  }
  return columns;
}

/**
 * Asegura que el esquema SQL y sus migraciones existan en la base de datos remota de Turso (una sola vez por conexión).
 * Las migraciones ALTER TABLE ADD COLUMN solo se envían si la columna realmente no existe en la base remota.
 * La migración de notas_generales (recrear tabla con FKs) solo corre si el guard detecta que faltan FKs.
 */
export async function ensureTursoSchema(schemaSql: string, force = false): Promise<void> {
  if (!clientInstance || !navigator.onLine) return;
  if (isTursoSchemaEnsured && !force) return;

  try {
    updateStatus('syncing');
    const statements = schemaSql
      .split(';')
      .map(s => s.trim())
      .filter(s => s.length > 0);

    // Fase 1a: DDL del esquema base (idempotente, CREATE TABLE IF NOT EXISTS)
    const baseDdlBatch: Array<{ sql: string; args: any[] }> = [
      { sql: 'PRAGMA foreign_keys = ON;', args: [] },
      ...statements.map(sql => ({ sql, args: [] })),
    ];
    await syncBatchExecuteRemote(baseDdlBatch);

    // Fase 1b: Migraciones (ALTER TABLE ADD COLUMN) - consultar estado real de columnas para no reaplicar si ya existen
    const pendingMigrations = await filterPendingMigrations(MIGRATIONS, getTursoTableColumns);
    if (pendingMigrations.length > 0) {
      const migrationsBatch = pendingMigrations.map(sql => ({ sql, args: [] }));
      await syncBatchExecuteRemote(migrationsBatch);
    }

    // Fase 2: Guard: chequear si notas_generales ya tiene FKs antes de recrear la tabla
    const fkCheckResult = await clientInstance!.execute("PRAGMA foreign_key_list(notas_generales)");
    const hasFk = Boolean(fkCheckResult && fkCheckResult.rows && fkCheckResult.rows.length > 0);

    if (!hasFk) {
      // La tabla existe pero no tiene FKs → recrear con FKs en un ÚNICO batch atómico
      const migrationBatch: Array<{ sql: string; args: any[] }> = [
        { sql: 'PRAGMA foreign_keys = OFF;', args: [] },
        { sql: `
          CREATE TABLE IF NOT EXISTS notas_generales_new (
              id TEXT PRIMARY KEY,
              titulo TEXT NOT NULL,
              contenido TEXT NOT NULL,
              carpeta_id TEXT,
              asignatura_id TEXT,
              evento_id TEXT,
              tags TEXT,
              en_papelera INTEGER DEFAULT 0,
              fecha_creacion TEXT NOT NULL,
              fecha_modificacion TEXT NOT NULL,
              FOREIGN KEY(asignatura_id) REFERENCES asignaturas(id) ON DELETE SET NULL,
              FOREIGN KEY(carpeta_id) REFERENCES carpetas_notas(id) ON DELETE SET NULL,
              FOREIGN KEY(evento_id) REFERENCES eventos_calendario(id) ON DELETE SET NULL
          );
        `, args: [] },
        { sql: `
          INSERT OR IGNORE INTO notas_generales_new (id, titulo, contenido, carpeta_id, asignatura_id, evento_id, tags, en_papelera, fecha_creacion, fecha_modificacion)
          SELECT id, titulo, contenido, carpeta_id, asignatura_id, evento_id, tags, en_papelera, fecha_creacion, fecha_modificacion FROM notas_generales;
        `, args: [] },
        { sql: "DROP TABLE notas_generales;", args: [] },
        { sql: "ALTER TABLE notas_generales_new RENAME TO notas_generales;", args: [] },
        { sql: "CREATE INDEX IF NOT EXISTS idx_notas_asignatura ON notas_generales(asignatura_id);", args: [] },
        { sql: "CREATE INDEX IF NOT EXISTS idx_notas_carpeta ON notas_generales(carpeta_id);", args: [] },
        { sql: 'PRAGMA foreign_keys = ON;', args: [] },
      ];
      await syncBatchExecuteRemote(migrationBatch);
      console.log('[TursoSync] Migración de notas_generales con FKs aplicada en Turso.');
    }

    isTursoSchemaEnsured = true;
    updateStatus('synced');
  } catch (err) {
    console.warn('[TursoSync] No se pudo sincronizar el esquema con Turso:', err);
    updateStatus('offline');
  }
}

